/* eslint-disable no-undef */
import { Navbar, TesSearch, Input } from "components";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getLongDate } from "utils";
import LogoLoader from "components/widgets/loader/LogoLoader";
import { IUser } from "types";
import { downloadLogo } from "assets/logos";
import { useFetchUsersQuery } from "services/getAllUsersApi";

const ITEMS_PER_PAGE = 9;

export const UsersView: React.FC = () => {
  const { data: usersResponse, isLoading, isError } = useFetchUsersQuery();
  const users = (usersResponse ?? []) as IUser[];

  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showPrevNext, setShowPrevNext] = useState<boolean>(false);
  const [currentUserIndex, setCurrentUserIndex] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
    setOpenUserId(null);
    setShowPrevNext(false);
    setCurrentUserIndex(0);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenUserId(null);
    setShowPrevNext(false);
    setCurrentUserIndex(0);
  };

  const handlePreviousUser = () => {
    if (currentUserIndex > 0) {
      const newIndex = currentUserIndex - 1;
      setCurrentUserIndex(newIndex);
      setOpenUserId(filteredUsers[newIndex].id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleNextUser = () => {
    if (currentUserIndex < filteredUsers.length - 1) {
      const newIndex = currentUserIndex + 1;
      setCurrentUserIndex(newIndex);
      setOpenUserId(filteredUsers[newIndex].id ?? null);
      const newPage = Math.floor(newIndex / ITEMS_PER_PAGE) + 1;
      setCurrentPage(newPage);
    }
  };

  const handleToggle = (id: string | undefined) => {
    if (id) {
      setOpenUserId(openUserId === id ? null : id);
      setShowPrevNext(openUserId !== id);
      if (openUserId !== id) {
        const index = filteredUsers.findIndex((user) => user.id === id);
        setCurrentUserIndex(index);
        const newPage = Math.floor(index / ITEMS_PER_PAGE) + 1;
        setCurrentPage(newPage);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  interface InfoRowProps {
    label: string;
    value?: React.ReactNode;
  }

  const InfoRow = ({ label, value }: InfoRowProps) => {
    return (
      <div className="flex items-start gap-3 rounded-md bg-white px-3 py-2 shadow-sm border border-gray-100">
        {/* Vertical Divider */}
        <span className="w-1 rounded-full bg-emerald-500 shrink-0" />

        <div className="leading-tight">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-0.5 text-sm font-semibold text-gray-800 break-words">{value ?? "—"}</p>
        </div>
      </div>
    );
  };

  // console.log(users);

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();

    const matchesFirstName =
      user.staffName?.firstName?.toLowerCase().includes(searchLower) ?? false;
    const matchesPosition = user.position?.toLowerCase().includes(searchLower) ?? false;
    const matchesSchool =
      user.schoolOfPresentPosting?.nameOfSchool?.toLowerCase().includes(searchLower) ?? false;
    const matchesEmail = user.email?.toLowerCase().includes(searchLower) ?? false;
    const matchesOgNumber = user.ogNumber?.toLowerCase().includes(searchLower) ?? false;

    return matchesFirstName || matchesPosition || matchesSchool || matchesEmail || matchesOgNumber;
  });

  if (isLoading) return <LogoLoader />;
  if (isError) return <p>{isError}</p>;

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  console.log(paginatedUsers);
  return (
    <>
      <Navbar />
      <div className="flex flex-col h-full w-full bg-gray-900 mt-16 gap-4 p-5">
        {/* HEADER ROW */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
          {/* LEFT: USERS COUNT */}
          <h4 className="text-white text-lg font-semibold">
            Total Number of Users:
            <span className="ml-2 text-green-400 font-bold">{users.length}</span>
          </h4>

          {/* RIGHT: SEARCH */}
          <div className="relative w-full md:w-[420px]">
            <Input
              size="md"
              onChange={handleChange}
              value={searchTerm}
              placeholder="Search by name, OG number or school"
              className="rounded-full h-10 pl-4 pr-10 text-base bg-white w-full"
            />
            <TesSearch
              size={22}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
          </div>
        </div>

        {paginatedUsers.length > 0 ? (
          paginatedUsers.map((user) => (
            <div
              key={user.id}
              className={`flex flex-col w-full ${
                openUserId && openUserId !== user.id ? "hidden" : ""
              }`}
            >
              <div
                className="flex flex-row justify-between items-center hover:bg-[#7ed348] hover:text-black bg-[#1b733f] text-white p-3 cursor-pointer rounded-lg shadow-md"
                onClick={() => {
                  handleToggle(user.id);
                }}
              >
                <p className="text-lg font-semibold">{user?.staffName?.firstName.toUpperCase()}</p>
                <h5 className="text-xl font-bold">{openUserId === user.id ? "-" : "+"}</h5>
              </div>
              <AnimatePresence>
                {openUserId === user?.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden w-full mt-2"
                  >
                    {/* ======================= USER DETAILS ======================= */}
                    <div className="flex flex-col space-y-6">
                      {/* ===== PERSONAL INFORMATION ===== */}
                      <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                            <span className="h-4 w-1 rounded bg-emerald-600" />
                            Personal Information
                          </h3>
                        </div>
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-gray-800 text-sm">
                          <InfoRow
                            label="Name"
                            value={`${user?.staffName?.firstName} ${user?.staffName?.lastName}`}
                          />
                          <InfoRow label="Gender" value={user?.ogNumber} />
                          <InfoRow label="Gender" value={user?.gender} />
                          <InfoRow label="Phone Number" value={user?.phoneNumber} />
                          <InfoRow label="Position" value={user?.position ?? "Staff"} />
                          <InfoRow label="Staff Type" value={user?.staffType} />
                          <InfoRow label="Cadre" value={user?.cadre} />
                          <InfoRow label="Nationality" value={user?.nationality} />
                          <InfoRow label="State of Origin" value={user?.stateOfOrigin} />
                          <InfoRow label="Local Government" value={user?.lgOfOrigin} />
                          <InfoRow label="Residential Address" value={user?.residentialAddress} />
                          <InfoRow label="Zone" value={user?.zone} />
                          <InfoRow label="Division" value={user?.division} />
                          <InfoRow
                            label="Date of Birth"
                            value={user?.dateOfBirth ? getLongDate(user.dateOfBirth) : "—"}
                          />
                          <InfoRow
                            label="Date of First Appointment"
                            value={
                              user?.dateOfFirstAppointment
                                ? getLongDate(user.dateOfFirstAppointment)
                                : "—"
                            }
                          />
                          <InfoRow
                            label="Date of Retirement"
                            value={
                              user?.dateOfRetirement ? getLongDate(user.dateOfRetirement) : "—"
                            }
                          />
                          <InfoRow label="Grade Level" value={user?.gradeLevel} />
                        </div>
                      </section>

                      {/* ===== POSTING INFORMATION ===== */}
                      <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                            <span className="h-4 w-1 rounded bg-emerald-600" />
                            Posting Information
                          </h3>
                        </div>
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-800 text-sm">
                          <InfoRow
                            label="School of Previous Posting"
                            value={user?.schoolOfPreviousPosting?.nameOfSchool ?? "—"}
                          />
                          <InfoRow
                            label="School of Present Posting"
                            value={user?.schoolOfPresentPosting?.nameOfSchool ?? "—"}
                          />
                          <InfoRow
                            label="Date of Present School Posting"
                            value={
                              user?.dateOfPresentSchoolPosting
                                ? getLongDate(user.dateOfPresentSchoolPosting)
                                : "—"
                            }
                          />
                          <InfoRow label="Service Status" value={user?.serviceStatus ?? "—"} />
                        </div>
                      </section>

                      {/* ===== SUBJECTS TAUGHT ===== */}
                      <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                            <span className="h-4 w-1 rounded bg-emerald-600" />
                            Subjects Taught
                          </h3>
                        </div>
                        <div className="p-4">
                          {user?.subjectsTaught?.length ? (
                            <div className="flex flex-wrap gap-2">
                              {user.subjectsTaught.map((s: string, i: number) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 font-medium"
                                >
                                  {s}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">—</p>
                          )}
                        </div>
                      </section>

                      {/* ===== QUALIFICATIONS ===== */}
                      <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                            <span className="h-4 w-1 rounded bg-emerald-600" />
                            Qualifications
                          </h3>
                        </div>
                        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {user?.qualifications?.length ? (
                            user.qualifications.map((q, i) => (
                              <div
                                key={i}
                                className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm space-y-1"
                              >
                                <InfoRow label="Institution" value={q.schoolName} />
                                <InfoRow label="Degree" value={q.degreeType} />
                                <InfoRow label="Specialization" value={q.specialization} />
                                <InfoRow label="Years" value={`${q.startYear} – ${q.endYear}`} />
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 italic">No qualifications recorded</p>
                          )}
                        </div>
                      </section>

                      {/* ===== DOCUMENTS ===== */}
                      <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                            <span className="h-4 w-1 rounded bg-emerald-600" />
                            Documents
                          </h3>
                        </div>
                        {/* <div className="p-4 flex items-center gap-4"> */}
                        {/* {user?.letters?.postingLetter ? ( */}
                        {user?.letters?.postingLetter ? (
                          <a
                            href={user?.letters?.postingLetter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 transition shadow"
                          >
                            <img
                              src={downloadLogo}
                              alt="Download Posting Letter"
                              className="h-8 w-8"
                            />
                          </a>
                        ) : (
                          <p className="text-gray-500">No documents uploaded</p>
                        )}
                      </section>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))
        ) : (
          <p className="text-white text-center">No user found matching your search.</p>
        )}

        {showPrevNext && (
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={handlePreviousUser}
              className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#1b733f]/80"
            >
              Previous User
            </button>
            <span className="text-white">
              User {currentUserIndex + 1} of {filteredUsers.length}
            </span>
            {currentUserIndex < filteredUsers.length - 1 && (
              <button
                onClick={handleNextUser}
                className="px-4 py-2 bg-[#1b733f] text-white rounded shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Next User
              </button>
            )}
          </div>
        )}

        {!showPrevNext && (
          <div className="flex justify-center mt-4">
            {currentPage !== 1 && (
              <button
                onClick={() => {
                  handlePageChange(currentPage - 1);
                }}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-l shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Previous
              </button>
            )}
            <span className="px-4 py-2 bg-[#1b733f] text-white shadow-md">
              Page {currentPage} of {totalPages}
            </span>
            {currentPage !== totalPages && (
              <button
                onClick={() => {
                  handlePageChange(currentPage + 1);
                }}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-[#1b733f] text-white rounded-r shadow-md hover:bg-[#1b733f]/80 hover:bg-[#7ed348] hover:text-black"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
